import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import styles from "../../../styles/patient-page-modules/addDeviceModal.module.css";
import DatePicker from "../../shared-modules/DatePicker/DatePicker";
import Input from "../../ui/input";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import Button from "../../button-v1";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useMutation,
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import { linkDevice } from "../../../services/patient/devices";
import { parseDateSafe } from "../../../utils/dateFormatters";
import toast from "react-hot-toast";
import { tenoviDevices } from "../../../utils/constants";
// const devices = [
//   { label: "Weight Scale", value: "Tenovi Scale" },
//   { label: "Tenovi Watch", value: "Tenovi Watch" },
//   { label: "Blood Pressure Monitor", value: "Tenovi Cellular BPM - L" },
//   { label: "Bariatric Scale", value: "Bariatric Scale" },
//   { label: "Peak Flow Meter", value: "Tenovi Peak Flow Meter" },
//   { label: "Cellular Glucometer", value: "Tenovi Cellular Glucometer" },
//   { label: "Cellular Scale", value: "Tenovi Cellular Scale" },
//   { label: "Pulse Oximeter", value: "Tenovi Pulse Ox" },
//   { label: "Blood Presure monitor (Large)", value: "Tenovi BPM - L" },
//   { label: "Cellular Pulse Oximeter", value: "Tenovi Cellular Pulse Ox" },
//   { label: "Glucometer", value: "Tenovi Glucometer" },
//   { label: "Thermometer", value: "Tenovi Thermometer" },
//   {
//     label: "Cellular Blood Pressure Monitor",
//     value: "Tenovi Cellular BPM - S",
//   },
//   { label: "Blood Presure monitor (Small)", value: "Tenovi BPM - S" },
// ];

const schema = {
  name: "",
  type: "cellular",
  imei: "",
  gateway_id: "",
  assigned_at: new Date().toISOString(),
};

const validationSchema = z.object({
  imei: z
    .string()
    .regex(/^[a-zA-Z0-9]+$/, "IMEI must contain only letters and numbers")
    .min(5, "IMEI must be between 5 and 20 characters")
    .max(20, "IMEI must be between 5 and 20 characters"),
  name: z.string().min(1, "Device name is required"),
  type: z.string().min(1, "Device type is required"),
  gateway_id: z.string().optional(),
  assigned_at: z.string("Assignment date is required"),
});

const AddDeviceModal = React.memo(({ isOpen, onClose, selectedDevice }) => {
  const queryClient = useQueryClient();

  const { mutate: addDeviceMutation, isLoading } = useMutation(
    (data) => linkDevice(data),
    {
      onSuccess: () => {
        toast.success("Device Linked");
        queryClient.invalidateQueries(["patient-assigned-devices"]);
        onClose();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || "Could not link device");
      },
    }
  );

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: schema,
    resolver: zodResolver(validationSchema),
  });

  const name = watch("name");
  const type = watch("type");
  const assigned_at = watch("assigned_at");

  const submitHandler = (values) => {
    if (type === "cellular") {
      delete values.gateway_id;
    }
    values.location_id = JSON.parse(localStorage.getItem("current_loc"))?.id;
    addDeviceMutation(values);
  };

  useEffect(() => {
    if (type === "cellular") setValue("gateway_id", "");
  }, [type]);
  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          {selectedDevice ? (
            <DialogTitle>Update Assigned Device</DialogTitle>
          ) : (
            <DialogTitle>Assign New Device</DialogTitle>
          )}
        </DialogHeader>
        <DialogBody>
          <form className={styles.input_wrapper}>
            <label>IMEI</label>
            <Input
              type="text"
              maxLength={20}
              name="imei"
              placeholder=""
              onKeyDown={(e) => {
                if (e.key === " ") e.preventDefault();
              }}
              {...register("imei")}
            />
            <span className="error-message">{errors?.imei?.message}</span>

            <label>Device Type</label>
            <Select onChange={(value) => setValue("type", value)} value={type}>
              <SelectTrigger>
                <SelectValue placeholder="Select device type">
                  <p>{type}</p>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem key="cellular" value="cellular">
                  Cellular
                </SelectItem>
                <SelectItem key="gateway" value="gateway">
                  Gateway
                </SelectItem>
              </SelectContent>
            </Select>
            <span className="error-message">{errors?.type?.message}</span>

            <label>Device Name</label>
            <Select onChange={(value) => setValue("name", value)} value={name}>
              <SelectTrigger>
                <SelectValue placeholder="Select a device">
                  <p>{name}</p>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {tenoviDevices
                  ?.filter((device) => device.type === type)
                  .map((device, index) => (
                    <SelectItem key={device.value} value={device.value}>
                      {device.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <span className="error-message">{errors?.name?.message}</span>

            {getValues("type") === "gateway" && (
              <>
                <label>Gateway/Serial No.</label>
                <Input
                  type="text"
                  name="gateway_id"
                  placeholder=""
                  {...register("gateway_id")}
                />
                <span className="error-message">
                  {errors?.gateway_id?.message}
                </span>
              </>
            )}

            <label>Assignment Date</label>
            <DatePicker
              onCancelSelect={() => setValue("assigned_at", "")}
              selectedDate={assigned_at ? parseDateSafe(assigned_at) : null}
              onDateSelect={(date) => {
                setValue("assigned_at", date.toISOString());
              }}
              placeholder="Select assignment date"
            />
            <span className="error-message">
              {errors?.assigned_at?.message}
            </span>

            <small>
            Double check you entered in the correct IMEI number
            </small>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submitHandler)}
            loading={isLoading}
            disabled={isLoading}
          >
            Add Device
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default AddDeviceModal;